<template>
  <v-card class="pa-4">
    <v-dialog v-model="showValidationDialog">
      <v-card class="search-modal">
        <h2>Validation Results</h2>
        <v-form class="search-form">
          <v-alert
            v-if="
              !!validationResult &&
              !!validationResult.errors &&
              validationResult.errors.length > 0
            "
            type="error"
          >
            <pre>{{ validationResult.errors.join("\r\n") }}</pre>
          </v-alert>
          <v-alert
            v-if="
              !!validationResult &&
              validationResult.warnings &&
              validationResult.warnings.length > 0
            "
            type="warning"
            outlined
          >
            <pre>{{ validationResult.warnings.join("\r\n") }}</pre>
          </v-alert>
          <v-btn
            large
            color="primary"
            width="125px"
            :disabled="!validationResult.canBeSaved"
            v-on:click="saveChanges"
          >
            <v-icon v-if="!validationResult.canBeSaved">mdi-alert</v-icon>
            Save
          </v-btn>
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showSearchLinkableMembersDialog" width="500px">
      <v-card class="search-modal">
        <h2 class="mb-3">Search & Link to a Member</h2>
        <p class="mb-6">
          Only dual or lifetime dual members can be linked to, and only if they don't already have two members.
        </p>
        <v-form class="search-form" @submit.prevent="onSearchLinkableMember">
          <v-text-field
            outlined
            prepend-inner-icon="mdi-magnify"
            v-model="searchId"
            label="Search Member ID"
          />
        </v-form>
        <p v-if="searchInfo.found == false && !searchInfo.error">Was not able to find a the member</p>
        <p v-else-if="!!searchInfo.error">{{searchInfo.error}}</p>
        <p v-else-if="!!searchInfo.data && !!searchInfo.data.personId && searchInfo.data.personId == personId">
          You're already linked to this member
        </p>
        <div v-else-if="!!searchInfo.data" class="d-flex flex-column align-start">
          <p>Member ID: {{searchInfo.data.memberId}}</p>
          <p v-if="!!searchInfo.data.personId">Person ID: {{searchInfo.data.personId}}</p>
          <p v-if="!!searchInfo.data.firstName">First Name: {{searchInfo.data.firstName}}</p>
          <p v-if="!!searchInfo.data.lastName">Last Name: {{searchInfo.data.lastName}}</p>
          <p v-if="!!searchInfo.data.phoneNumber">Phone Number: {{searchInfo.data.phoneNumber}}</p>
          <p v-if="!!searchInfo.data.email">Email: {{searchInfo.data.email}}</p>
          <div class="d-flex justify-space-between align-center" style="width: 100%;">
            <div v-if="!!searchInfo.data.personId">
              <input type="checkbox" id="autofill-address" v-model="autofillAddress" />
              <label for="autofill-address">
                Autofill address
                <v-tooltip slot="append" dark top>
                  <template v-slot:activator="{on, attrs}">
                    <font-awesome-icon
                      icon="info-circle"
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                    />&nbsp;
                  </template>
                  <span>
                    &nbsp;This will apply the primary members' address to this member
                  </span>
                </v-tooltip>
              </label>
            </div>
            <v-btn
              large
              color="primary"
              width="125px"
              v-on:click="onLinkMember"
            >
              LINK
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
    <v-row class="mb-3">
      <h3 v-if="memberInfo.memberId">
        Member Details - {{ memberInfo.memberId }}
      </h3>
      <h3 v-else>Create New Member</h3>
    </v-row>
    <br />
    <v-row>
      <v-textarea
        :disabled="!isEditMode"
        :visible="!isEditMode"
        outlined
        auto-grow
        clearable
        rows="1"
        v-model="memberInfo.adminNotes"
        label="Description of changes (required)"
      />
      <v-btn
        class="ml-1 mr-1"
        :disabled="
          isEditMode &&
          (!memberInfo.adminNotes || /^\s*$/.test(memberInfo.adminNotes))
        "
        color="primary"
        @click="toggleEditMode"
      >
        {{ isEditMode ? "Save" : "Edit" }}
      </v-btn>
    </v-row>
    <v-row>
      <v-col>
        <v-card class="pa-2" elevation="2">
          <v-card-title class="text-h6"> Member ID &amp; Type </v-card-title>
          <v-card-text>
            <v-text-field
              v-model="memberInfo.memberId"
              label="Member ID"
              disabled
            />
            <v-select
              v-model="selectedMemberType"
              :items="possibleMemberTypes"
              label="Member Type*"
              v-on:change="(e) => changeExpirationDate()"
              :disabled="!isEditMode"
            ></v-select>
            <div class="text-center mb-3">
              <div class="my-2">
                <v-btn
                  color="primary"
                  elevation="2"
                  v-on:click="generateMemberId"
                  :disabled="!isEditMode || !selectedMemberType"
                >
                  Generate new Member ID
                </v-btn>
              </div>
              <div v-if="isEditMode && canLinkToAnotherMember" class="my-2">
                <v-btn
                  color="primary"
                  elevation="2"
                  v-on:click="() => showSearchLinkableMembersDialog = true"
                >
                  Link to an Existing Member
                </v-btn>
              </div>
            </div>
          </v-card-text>
          <br />
        </v-card>
      </v-col>
      <v-col>
        <v-card class="pa-2" elevation="2">
          <v-card-title class="text-h6">Member Dates</v-card-title>
          <v-card-text>
            <v-row>
              <v-col>
                <datepicker
                  :disabled="!isEditMode"
                  :date="memberInfo.membershipStartDate"
                  label="Member Since Date"
                  v-on:updateDates="updateDates($event, 'membershipStartDate')"
                />
                <datepicker
                  :disabled="!isEditMode"
                  :date="memberInfo.consecutiveMembershipStartDate"
                  label="Member Start Date"
                  v-on:updateDates="
                    updateDates($event, 'consecutiveMembershipStartDate')
                  "
                />
                <datepicker
                  :disabled="!isEditMode"
                  :date="memberInfo.membershipPostDate"
                  label="Member Post Date"
                  v-on:updateDates="updateDates($event, 'membershipPostDate')"
                />
              </v-col>
              <v-col>
                <datepicker
                  v-if="
                    selectedMemberType !== 'Lifetime' &&
                    selectedMemberType !== 'Lifetime Dual'
                  "
                  :disabled="!isEditMode"
                  :date="memberInfo.membershipExpiryDate"
                  label="Member Expiry Date"
                  v-on:updateDates="
                    ($event) => updateDates($event, 'membershipExpiryDate')
                  "
                />
                <datepicker
                  :disabled="!isEditMode"
                  :date="memberInfo.aussieTimesSubscriptionExpiryDate"
                  label="Aussie Times Expiration Date"
                  v-on:updateDates="
                    updateDates($event, 'aussieTimesSubscriptionExpiryDate')
                  "
                />  
                <v-checkbox
                  v-model="memberInfo.aussieTimesFirstClassPostage" 
                  :disabled="!isEditMode"
                  label="1st Class Postage"
                  />
              </v-col>
            </v-row>
            <br />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-divider class="my-3" />
    <div class="mt-3">
      <v-row class="pt-0 mt-0">
        <v-col>
          <h3>Payments</h3>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-data-table :headers="paymentHeaders" :items="memberInfo.payments">
            <template v-slot:item.transactionAmount="{ item }">
              <span>${{ item.transactionAmount }}</span>
            </template>
            <template v-slot:item.captured="{ item }">
              <v-tooltip dark right>
                <template v-slot:activator="{ on, attrs }">
                  <font-awesome-icon
                    icon="credit-card"
                    :color="
                      item.captured ? 'green' : item.authorized ? 'gray' : 'red'
                    "
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span>{{
                  item.captured
                    ? "Paid"
                    : item.authorized
                    ? "Not Captured"
                    : "Payment Rejected"
                }}</span>
              </v-tooltip>
            </template>
            <template v-slot:item.authorized="{ item }">
              <v-tooltip dark right>
                <template v-slot:activator="{ on, attrs }">
                  <font-awesome-icon
                    icon="credit-card"
                    :color="item.authorized ? 'green' : 'red'"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span>{{
                  item.authorized ? "Authorized" : "Authorization Rejected"
                }}</span>
              </v-tooltip>
            </template>
            <template v-slot:item.paidAt="{ item }">
              <span v-if="!!item.paidAt">{{
                $dateUtils.formatDateTime(item.paidAt)
              }}</span>
            </template>
            <template v-slot:item.requestType="{ item }">
              <span>{{ requestTypeText(item.requestType) }}</span>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </div>
    <v-divider class="my-3" />
    <div class="mt-3">
      <v-row class="pt-0 mt-0">
        <v-col>
          <h3>Notes</h3>
        </v-col>
      </v-row>
      <v-row>
        <v-textarea
          disabled
          @input="updateText('notes', $event)"
          :value="memberInfo.notes"
          outlined
          rows="1"
          auto-grow
        ></v-textarea>
      </v-row>
    </div>
    <v-snackbar v-model="showInfoMessage" title color="green">
      {{ infoMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="primary"
          text
          v-bind="attrs"
          @click="showInfoMessage = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="showErrorMessage" title color="red">
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="primary"
          text
          v-bind="attrs"
          @click="showErrorMessage = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script lang="ts">
import Vue from "vue";
import Datepicker from "../../common/inputs/DatepickerFormatted.vue";

import memberService from "@/services/admin/memberService";

import { Messages } from "@/constants";
import { MemberType } from "@/types/Member";
import { AdminMemberType } from "@/types/admin/Member";
import { AdminMemberInfo } from '@/types/admin/Person';

export default Vue.extend({
  name: "MemberDetails",
  props: {
    personId: { type: Number },
  },
  components: {
    Datepicker,
  },
  data: function () {
    return {
      memberInfo: {} as AdminMemberInfo,
      isEditMode: false,
      showValidationDialog: false,
      validationResult: {} as any,
      memberTypes: [] as AdminMemberType[],
      selectedMemberType: "",
      possibleMemberTypes: [] as string[],
      reloadPage: false,
      errorMessage: "",
      infoMessage: "",
      showErrorMessage: false,
      showInfoMessage: false,
      searchId: "",
      autofillAddress: false,
      searchInfo: {
        found: null,
        error: "",
        data: null,
      },
      canLinkToAnotherMember: false,
      showSearchLinkableMembersDialog: false,
      paymentHeaders: [
        { text: "Member ID", align: "left", value: "memberId" },
        { text: "Dollar Amount", align: "left", value: "transactionAmount" },
        { text: "Captured", align: "left", value: "captured" },
        { text: "Authorized", align: "left", value: "authorized" },
        { text: "Paid At", align: "left", value: "paidAt" },
        { text: "Request Type", align: "left", value: "requestType" },
        { text: "Notes", align: "left", value: "notes" },
      ],
    };
  },
  computed: {
    formattedMemberInfo(): AdminMemberInfo {
      const data = { ...this.memberInfo };

      switch (this.memberInfo.membershipType) {
        case MemberType.Lifetime:
        case MemberType.LifetimeDual:
          if (!!data.membershipExpiryDate) data.membershipExpiryDate = null;
          break;
        case MemberType.Service:
        if (!!data.aussieTimesSubscriptionExpiryDate) data.aussieTimesSubscriptionExpiryDate = null;
          break;
      }

      return data;
    }
  },
  created: function () {
    if (this.personId) {
      memberService
        .getMemberByPersonId(this.personId)
        .then((res: AdminMemberInfo) => {
          if (res) {
            this.memberInfo = res;
            if (res.memberId) {
              this.selectedMemberType =
                this.memberInfo.membershipTypeDescription;
              this.reloadPage = false;
            } else {
              const today = new Date().toISOString() as any;
              const expirationDate = new Date();
              expirationDate.setDate(new Date().getDate() + 365);

              this.memberInfo.consecutiveMembershipStartDate = today;
              this.memberInfo.membershipStartDate = today;
              this.memberInfo.membershipPostDate = today;
              this.memberInfo.membershipExpiryDate =
                expirationDate.toISOString() as any;

              this.isEditMode = true;
              this.reloadPage = true;
            }
          }
        })
        .catch((e) => {
          this.memberInfo = {} as AdminMemberInfo;
        });

      this.possibleMemberTypes.push("");
      memberService.getMembershipTypes().then((res: any) => {
        this.memberTypes = res;
        for (let i = 0; i < res.length; i++) {
          this.possibleMemberTypes.push(res[i].membershipTypeDescription);
        }
      });
    } else {
      this.errorMessage = "Invalid data.";
    }
  },
  watch: {
    personId: function (newVal: String) {
      memberService.getMemberByPersonId(this.personId).then((res) => {
        this.memberInfo = res;
        this.selectedMemberType = this.memberInfo.membershipTypeDescription;
      });
    },
    selectedMemberType: function (newValue: String) {
      switch (newValue) {
        case 'Dual':
        case 'LifetimeDual':
          this.canLinkToAnotherMember = true;
          break;
        default:
          this.canLinkToAnotherMember = false;
          break;
      }
    },
  },
  methods: {
    requestTypeText(type: number) {
      switch (type) {
        case 1:
          return "Registration";
        case 2:
          return "Renewal";
        case 3:
          return "Aussie Times Renewal";
        default:
          return "Unknown Request Type";
      }
    },
    onSearchLinkableMember: async function () {
      const response = await memberService.searchLinkableMember({ memberId: this.searchId });
      this.searchInfo = response;
    },
    onLinkMember: async function () {
      this.showSearchLinkableMembersDialog = false;

      const response = await memberService.linkToMember({ personId: this.personId, memberId: this.searchId, autofillAddress: this.autofillAddress });

      if (response.success)
      {
        this.searchId = "";
        this.autofillAddress = false;
        this.searchInfo = { found: null, error: "", data: null };
        window.location.reload();
      } else if (response.errorMessage) this.searchInfo.error = response.errorMessage;
    },
    reloadData: function () {
      if (this.personId) {
        memberService.getMemberByPersonId(this.personId).then((res) => {
          this.memberInfo = res;
          this.selectedMemberType = this.memberInfo.membershipTypeDescription;
        });
      } else {
        this.memberInfo = {} as AdminMemberInfo;
        this.isEditMode = true;
      }
      this.showValidationDialog = false;
      this.errorMessage = "";
      this.showErrorMessage = false;
      this.validationResult = {};
    },
    saveChanges: function () {
      this.errorMessage = "";
      this.showErrorMessage = false;
      this.showValidationDialog = false;
      this.$store.dispatch("changeLoaderStatus", true);
      memberService
        .editMember(this.formattedMemberInfo)
        .then((res) => {
          if (res.success) {
            this.reloadData();
            if (this.reloadPage) {
              window.location.reload();
            }

            this.infoMessage = Messages.SaveSuccessful;
            this.showInfoMessage = true;
          } else {
            this.errorMessage =
              Messages.SaveError + " " + res.messages.join(", ");
            this.showErrorMessage = true;
          }
        })
        .catch((e) => {
          this.errorMessage = Messages.SaveError;
          this.showErrorMessage = true;
        })
        .finally(() => this.$store.dispatch("changeLoaderStatus", false));
    },
    updateDates: function (val: any, key: string) {
      this.$data.memberInfo[key] = val?.date;

      if (this.isEditMode && key === "membershipStartDate") {
        let expDate = null;
        let ausExpDate = null;

        if (val.date) {
          const startDate = new Date(val.date);
          const expirationDate = new Date(val.date);
          expirationDate.setDate(startDate.getDate() + 365);

          expDate = expirationDate.toISOString() as any;

          switch (this.selectedMemberType) {
            case "Service":
              ausExpDate = null;
              break;
            case "Junior":
              ausExpDate = startDate.toISOString() as any;
              break;
            default:
              ausExpDate = expirationDate.toISOString() as any;
              break;
          }
        }

        this.$data.memberInfo.membershipExpiryDate = expDate;
        this.$data.memberInfo.aussieTimesSubscriptionExpiryDate = ausExpDate;
      }

      this.$data.memberInfo = Object.assign({}, this.$data.memberInfo);
    },
    updateText: function (key: string, event: string) {
      this.$data.memberInfo[key] = event;
    },
    toggleEditMode: function () {
      if (this.isEditMode) {
        this.validateAndConfirmSavingChanges();
      }
      this.isEditMode = !this.isEditMode;
    },
    validateAndConfirmSavingChanges: function () {
      this.errorMessage = "";
      this.showErrorMessage = false;
      if (!this.validateMemberType() || !this.validateFields()) {
        this.isEditMode = false; //this will toggle it to true
        return false;
      }
      memberService.validateMemberEdit(this.formattedMemberInfo).then((res: any) => {
        this.validationResult = res;
        if (
          this.validationResult.errors.length > 0 ||
          this.validationResult.warnings.length > 0
        ) {
          this.showValidationDialog = true;
        } else {
          this.saveChanges();
          this.validationResult = {};
          this.isEditMode = false; //this will toggle it to true
        }
      });
    },
    validateMemberType: function () {
      if (this.selectedMemberType == "") {
        this.errorMessage = "Please select a member type";
        this.showErrorMessage = true;
        return false;
      }
      var memberTypeSelected = this.memberTypes.find(
        (x) => x.membershipTypeDescription === this.selectedMemberType
      );
      if (memberTypeSelected) {
        this.memberInfo.membershipType = memberTypeSelected.membershipType;
        this.memberInfo.membershipTypeDescription =
          memberTypeSelected.membershipTypeDescription;
      } else {
        this.errorMessage = "Please select a member type";
        this.showErrorMessage = true;
        return false;
      }

      return true;
    },
    validateFields() {
      if (
        this.memberInfo.membershipExpiryDate == null &&
        this.selectedMemberType != "Lifetime" &&
        this.selectedMemberType != "Lifetime Dual"
      ) {
        this.errorMessage = "Member Expiration Date is required.";
        this.showErrorMessage = true;
        return false;
      }
      return true;
    },
    generateMemberId: function () {
      if (this.validateMemberType()) {
        memberService
          .generateMemberId({ membershipType: this.memberInfo.membershipType })
          .then((res: any) => {
            this.memberInfo.memberId = res.id;
            if (res.success && res.id) {
              this.memberInfo.memberId = res.id;
              this.errorMessage = "";
              this.showErrorMessage = false;
            } else {
              this.errorMessage = "Could not generate new ID.";
              this.showErrorMessage = true;
            }
          });
      }
    },
    changeExpirationDate() {
      if (this.isEditMode) {
        let startDate = new Date();

        if (this.$data.memberInfo.membershipStartDate)
          startDate = new Date(this.$data.memberInfo.membershipStartDate);

        const expirationDate = new Date(startDate);
        expirationDate.setDate(startDate.getDate() + 365);

        switch (this.selectedMemberType) {
          case "Lifetime":
          case "Lifetime Dual":
            this.$data.memberInfo.membershipExpiryDate = null;
            this.$data.memberInfo.aussieTimesSubscriptionExpiryDate =
              expirationDate.toISOString() as any;
            break;
          case "Service":
            if (this.$data.memberInfo.membershipExpiryDate == null)
              this.$data.memberInfo.membershipExpiryDate =
                expirationDate.toISOString() as any;

            this.$data.memberInfo.aussieTimesSubscriptionExpiryDate = null;
            break;
          case "Junior":
            if (this.$data.memberInfo.membershipExpiryDate == null)
              this.$data.memberInfo.membershipExpiryDate =
                expirationDate.toISOString() as any;

            this.$data.memberInfo.aussieTimesSubscriptionExpiryDate =
              startDate.toISOString() as any;
            break;
          default:
            if (this.$data.memberInfo.membershipExpiryDate == null)
              this.$data.memberInfo.membershipExpiryDate =
                expirationDate.toISOString() as any;

            this.$data.memberInfo.aussieTimesSubscriptionExpiryDate =
              expirationDate.toISOString() as any;
            break;
        }
      }
    },
  },
});
</script>